// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

// =============================================================================
// STORE (seller)
// =============================================================================

const DELIVERY_OPTIONS = [
  { id: "self", label: "Self delivery", icon: "truck" },
  { id: "pickup", label: "In-store pickup", icon: "map-pin" },
  { id: "courier", label: "Courier service", icon: "package" },
];

const PAYMENT_ACCEPTED = [
  { id: "mtn", label: "MTN MoMo", icon: "smartphone" },
  { id: "airtel", label: "Airtel Money", icon: "smartphone" },
  { id: "visa", label: "Card (Visa/Mastercard)", icon: "credit-card" },
];

export function StorePanel({ user, seller }) {
  const { categories } = useCategories();
  const [form, setForm] = useState({
    store_name: seller?.store_name || "",
    description: seller?.description || "",
    address: seller?.address || "",
    category: seller?.category || "",
    logo_url: seller?.logo_url || "",
    cover_url: seller?.cover_url || "",
    payment_methods: Array.isArray(seller?.payment_methods) ? seller.payment_methods : [],
    delivery_options: Array.isArray(seller?.delivery_options) ? seller.delivery_options : [],
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({ logo: false, cover: false });

  async function upload(kind, file) {
    if (!file) return;
    setError(null);
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${user.id}/store-${kind}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true, contentType: file.type || `image/${ext}` });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      setForm((f) => ({ ...f, [kind === "logo" ? "logo_url" : "cover_url"]: data.publicUrl }));
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  }

  function togglePayment(id) {
    setForm((f) => ({
      ...f,
      payment_methods: f.payment_methods.includes(id)
        ? f.payment_methods.filter((x) => x !== id)
        : [...f.payment_methods, id],
    }));
  }

  function toggleDelivery(id) {
    setForm((f) => ({
      ...f,
      delivery_options: f.delivery_options.includes(id)
        ? f.delivery_options.filter((x) => x !== id)
        : [...f.delivery_options, id],
    }));
  }

  async function save() {
    if (!form.store_name.trim()) {
      setError("Store name is required.");
      return;
    }
    if (!form.category) {
      setError("Please select a category.");
      return;
    }
    setSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from("sellers")
      .update({
        store_name: form.store_name.trim(),
        description: form.description.trim() || null,
        address: form.address.trim() || null,
        category: form.category,
        logo_url: form.logo_url || null,
        cover_url: form.cover_url || null,
        payment_methods: form.payment_methods,
        delivery_options: form.delivery_options,
        updated_at: new Date().toISOString(),
      })
      .eq("id", seller.id);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (!seller) return <EmptyState icon="store" title="No store linked" body="This account is not a seller." />;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={0} style={{ overflow: "hidden" }}>
        <div style={{
          height: 160,
          position: "relative",
          background: form.cover_url ? `url(${form.cover_url}) center/cover` : `linear-gradient(135deg, ${C.hunter}, ${C.hunterLt})`,
        }}>
          <label style={{
            position: "absolute", top: 12, right: 12,
            padding: "8px 12px", borderRadius: 10,
            background: "rgba(28,28,28,0.7)", color: C.dust,
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Icon name="upload" size={13} color={C.dust} />
            {uploading.cover ? "Uploading…" : form.cover_url ? "Change cover" : "Upload cover"}
            <input type="file" accept="image/*" onChange={(e) => upload("cover", e.target.files?.[0])} style={{ display: "none" }} />
          </label>
        </div>
        <div style={{ padding: "16px 22px 22px", display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{ position: "relative", marginTop: -50 }}>
            <div style={{
              width: 80, height: 80, borderRadius: 18,
              background: form.logo_url ? `url(${form.logo_url}) center/cover` : C.hunter,
              color: C.dust,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 30, fontWeight: 900,
              border: `4px solid ${C.white}`,
            }}>
              {!form.logo_url && (form.store_name?.[0] || "S").toUpperCase()}
            </div>
            <label style={{
              position: "absolute", bottom: -2, right: -2,
              width: 26, height: 26, borderRadius: "50%",
              background: C.hunter, color: C.dust,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", border: `2px solid ${C.white}`,
            }}>
              <Icon name="upload" size={11} color={C.dust} />
              <input type="file" accept="image/*" onChange={(e) => upload("logo", e.target.files?.[0])} style={{ display: "none" }} />
            </label>
          </div>
          <div style={{ flex: 1, paddingTop: 8 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>
              {uploading.logo ? "Uploading logo…" : "Logo & cover are shown on your storefront."}
            </div>
          </div>
        </div>
      </Card>

      <Card pad={22}>
        <Eyebrow>Store info</Eyebrow>
        <div style={{ display: "grid", gap: 14, marginTop: 12 }}>
          <Field label="Store name"><input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} style={inputStyle} /></Field>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Tell customers what your store is about…" />
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle}>
              <option value="">Select a category</option>
              {categories.filter((c) => c.id !== "all").map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Address / pickup location">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} style={inputStyle} placeholder="e.g. Unit 14, Arcades Mall, Lusaka" />
          </Field>
        </div>
      </Card>

      <Card pad={22}>
        <Eyebrow>Payment methods accepted</Eyebrow>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>Customers will only see these options at checkout.</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
          {PAYMENT_ACCEPTED.map((p) => {
            const on = form.payment_methods.includes(p.id);
            return (
              <button key={p.id} onClick={() => togglePayment(p.id)} style={{
                all: "unset", cursor: "pointer",
                padding: "12px 14px", borderRadius: 12,
                background: on ? C.greenBg : C.white,
                border: on ? `2px solid ${C.hunter}` : "1px solid rgba(196,193,183,0.4)",
                display: "flex", alignItems: "center", gap: 10,
                fontSize: 12.5, fontWeight: on ? 800 : 600,
                color: on ? C.hunter : C.slate,
              }}>
                <Icon name={p.icon} size={14} color={on ? C.hunter : C.slate} />
                {p.label}
                {on && <Icon name="check" size={13} color={C.hunter} style={{ marginLeft: "auto" }} />}
              </button>
            );
          })}
        </div>
      </Card>

      <Card pad={22}>
        <Eyebrow>Delivery options</Eyebrow>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>How customers receive their orders.</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 12 }}>
          {DELIVERY_OPTIONS.map((d) => {
            const on = form.delivery_options.includes(d.id);
            return (
              <button key={d.id} onClick={() => toggleDelivery(d.id)} style={{
                all: "unset", cursor: "pointer",
                padding: "12px 14px", borderRadius: 12,
                background: on ? C.greenBg : C.white,
                border: on ? `2px solid ${C.hunter}` : "1px solid rgba(196,193,183,0.4)",
                display: "flex", alignItems: "center", gap: 10,
                fontSize: 12.5, fontWeight: on ? 800 : 600,
                color: on ? C.hunter : C.slate,
              }}>
                <Icon name={d.icon} size={14} color={on ? C.hunter : C.slate} />
                {d.label}
                {on && <Icon name="check" size={13} color={C.hunter} style={{ marginLeft: "auto" }} />}
              </button>
            );
          })}
        </div>
      </Card>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Btn>
        {saved && <span style={{ fontSize: 12, color: C.greenDk, fontWeight: 700 }}>Saved ✓</span>}
        {error && <span style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>{error}</span>}
      </div>
    </div>
  );
}
